import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  FormControlLabel,
  Checkbox,
  Alert,
  Grid,
  Divider,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import {
  ArrowForward,
  ArrowBack,
  Security,
  VerifiedUser,
  FamilyRestroom,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import ChildRegistrationForm from '../components/ChildRegistrationForm';

const CURRENT_YEAR = new Date().getFullYear();

const SignUpPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const [activeStep, setActiveStep] = useState(0);
  const [showChildRegistration, setShowChildRegistration] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    birthYear: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false,
    agreeToPrivacy: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (authService.isAuthenticated()) {
      const user = authService.getCurrentUser();
      if (user) {
        const routes: Record<string, string> = {
          PARENT: '/parent-dashboard',
          STUDENT: '/student-dashboard',
          TEACHER: '/teacher-dashboard',
          TUTOR: '/tutor-dashboard',
        };
        navigate(routes[user.userType] ?? '/', { replace: true });
      }
    }
  }, [navigate]);

  const steps = ['Your Details', 'Account Setup', 'Review & Complete'];

  const parsedBirthYear = formData.birthYear ? parseInt(formData.birthYear, 10) : null;
  const ageEstimate = parsedBirthYear ? CURRENT_YEAR - parsedBirthYear : null;
  const isLikelyUnderSixteen = ageEstimate !== null && ageEstimate < 16;

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateStep = (): boolean => {
    const newErrors: Record<string, string> = {};

    switch (activeStep) {
      case 0:
        if (!formData.firstName) newErrors.firstName = 'First name is required';
        if (!formData.lastName) newErrors.lastName = 'Last name is required';
        if (!formData.email) {
          newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
          newErrors.email = 'Please enter a valid email address';
        }
        if (!formData.birthYear) {
          newErrors.birthYear = 'Year of birth is required';
        } else {
          const y = parseInt(formData.birthYear, 10);
          if (isNaN(y) || y < 1900 || y > CURRENT_YEAR) {
            newErrors.birthYear = 'Please enter a valid year (e.g. 1995)';
          }
        }
        break;
      case 1:
        if (formData.password) {
          if (formData.password.length < 8) {
            newErrors.password = 'Password must be at least 8 characters';
          }
          if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
          }
        }
        break;
      case 2:
        if (!formData.agreeToTerms) {
          newErrors.agreeToTerms = 'You must agree to the terms and conditions';
        }
        if (!formData.agreeToPrivacy) {
          newErrors.agreeToPrivacy = 'You must agree to the privacy policy';
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep()) {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    setLoading(true);
    setError(null);
    try {
      await authService.register({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password || undefined,
        birthYear: parseInt(formData.birthYear, 10),
      });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err: any) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChildRegistration = async (_childData: any) => {
    // Placeholder — child registration is handled from parent dashboard post-login
  };

  if (success) {
    return (
      <>
        <Container maxWidth="sm">
          <Box sx={{ mt: 8, textAlign: 'center' }}>
            <Alert severity="success" sx={{ mb: 3 }}>
              Account created successfully! Please check your email to verify your account.
              You can sign in after verification. Redirecting to sign in...
            </Alert>
          </Box>
        </Container>

        {showChildRegistration && (
          <Dialog open={showChildRegistration} onClose={() => setShowChildRegistration(false)} maxWidth="md" fullWidth>
            <DialogTitle>Register Your Child</DialogTitle>
            <DialogContent>
              <ChildRegistrationForm
                onRegister={handleChildRegistration}
                onCancel={() => setShowChildRegistration(false)}
                title="Register Your Child"
                submitButtonText="Register Child"
                showStepper={true}
                mode="register"
              />
            </DialogContent>
          </Dialog>
        )}
      </>
    );
  }

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h5" sx={{ mb: 1, textAlign: 'center' }}>
              Your Details
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
              Create one account — you can use it as a parent, student, or both.
            </Typography>

            <Alert severity="info" icon={<FamilyRestroom />} sx={{ mb: 3 }}>
              <strong>Registering for a child under 16?</strong> Create your own parent account
              first. After signing in you can add child accounts from the parent dashboard.
            </Alert>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={formData.firstName}
                  onChange={e => handleInputChange('firstName', e.target.value)}
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={formData.lastName}
                  onChange={e => handleInputChange('lastName', e.target.value)}
                  error={!!errors.lastName}
                  helperText={errors.lastName}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={formData.email}
                  onChange={e => handleInputChange('email', e.target.value)}
                  error={!!errors.email}
                  helperText={errors.email}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Year of Birth"
                  placeholder="e.g. 1995"
                  inputProps={{ inputMode: 'numeric', maxLength: 4 }}
                  value={formData.birthYear}
                  onChange={e => handleInputChange('birthYear', e.target.value.replace(/\D/g, ''))}
                  error={!!errors.birthYear}
                  helperText={errors.birthYear || 'Used for age verification only'}
                />
              </Grid>
            </Grid>

            {isLikelyUnderSixteen && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Users under 16 cannot register independently. A parent must create their account
                via the parent dashboard after signing in.
              </Alert>
            )}
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h5" sx={{ mb: 3, textAlign: 'center' }}>
              Account Setup
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={formData.password}
                  onChange={e => handleInputChange('password', e.target.value)}
                  error={!!errors.password}
                  helperText={errors.password || 'At least 8 characters'}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Confirm Password"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={e => handleInputChange('confirmPassword', e.target.value)}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword}
                />
              </Grid>
            </Grid>
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Button
                variant="text"
                onClick={() => {
                  handleInputChange('password', '');
                  handleInputChange('confirmPassword', '');
                  setErrors({});
                }}
                sx={{ textTransform: 'none' }}
              >
                Skip — I'll sign in with an email code instead
              </Button>
            </Box>
            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Typography variant="body2" color="text.secondary">
                <Security sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                Password is optional. You can always sign in with a 6-digit code sent to your email.
              </Typography>
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h5" sx={{ mb: 3, textAlign: 'center' }}>
              Review & Complete
            </Typography>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Name</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {formData.firstName} {formData.lastName}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Email</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {formData.email}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Year of Birth</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {formData.birthYear}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Sign-in Method</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {formData.password ? 'Password' : 'Email code'}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Typography variant="body2" color="text.secondary">
                After creating your account you'll need to verify your email. You can then sign in
                and choose which role to use — student, parent, or others you activate later.
              </Typography>
            </Box>

            <Divider sx={{ mb: 3 }} />

            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.agreeToTerms}
                    onChange={e => handleInputChange('agreeToTerms', e.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2">
                    I agree to the{' '}
                    <Link to="/terms-of-service" style={{ color: theme.palette.primary.main }}>
                      Terms of Service
                    </Link>
                  </Typography>
                }
              />
              {errors.agreeToTerms && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {errors.agreeToTerms}
                </Alert>
              )}
            </Box>
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.agreeToPrivacy}
                    onChange={e => handleInputChange('agreeToPrivacy', e.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2">
                    I agree to the{' '}
                    <Link to="/privacy-policy" style={{ color: theme.palette.primary.main }}>
                      Privacy Policy
                    </Link>
                  </Typography>
                }
              />
              {errors.agreeToPrivacy && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {errors.agreeToPrivacy}
                </Alert>
              )}
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
      <Container maxWidth="md">
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 600, mb: 2 }}>
            Join StudyStride
          </Typography>
          <Typography variant="h6" color="text.secondary">
            One account — multiple roles. Choose how you want to use it when you sign in.
          </Typography>
        </Box>

        <Card sx={{ p: { xs: 2, md: 4 } }}>
          <CardContent>
            <Stepper activeStep={activeStep} sx={{ mb: 4, display: { xs: 'none', md: 'flex' } }}>
              {steps.map(label => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {isMobile && (
              <Box sx={{ mb: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Step {activeStep + 1} of {steps.length}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {steps[activeStep]}
                </Typography>
              </Box>
            )}

            {renderStepContent()}

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                startIcon={<ArrowBack />}
                sx={{ display: activeStep === 0 ? 'none' : 'flex' }}
              >
                Back
              </Button>
              <Box sx={{ flex: '1' }} />
              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  endIcon={loading ? <CircularProgress size={20} /> : <VerifiedUser />}
                  size="large"
                  disabled={loading}
                >
                  {loading ? 'Creating Account...' : 'Create Account'}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  endIcon={<ArrowForward />}
                  size="large"
                >
                  Next
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>

        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography variant="body1" color="text.secondary">
            Already have an account?{' '}
            <Link to="/login" style={{ color: theme.palette.primary.main, textDecoration: 'none' }}>
              Sign in here
            </Link>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default SignUpPage;
